import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { authService } from "../../services/auth";
import { projectsService } from "../../services/projects";
import { authService as auth } from "../../services/auth";
import {
  setCurrentProject,
} from "../../redux/project/projectSlice";
import { setCurrentBrand, setBrands } from "../../redux/brand/brandSlice";
import { projectsApi, useGetProjectsQuery } from "../../redux/api/projectsApi";
import { getApiErrorMessage } from "../../utils/apiErrors";
import { trackEvent } from "../../utils/tracking";

const Account = ({ authenticated, currentUser }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentProject, projects, loaded: projectsLoaded } = useSelector(
    (state) => state.project,
  );
  const { refetch: refetchProjects } = useGetProjectsQuery();

  useEffect(() => {
    refetchProjects();
  }, [refetchProjects]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  const handleCreateProject = (e) => {
    e.preventDefault();
    if (!newProjectName.trim()) {
      setCreateError("Project name is required");
      return;
    }

    setCreating(true);
    setCreateError(null);

    projectsService.createProject(
      newProjectName.trim(),
      (response) => {
        const newProject = response.data;
        setNewProjectName("");
        setShowCreateModal(false);
        setCreateError(null);
        setCreating(false);
        trackEvent("create_project", {
          project_hash: newProject?.hash,
          project_name: newProject?.name,
        });

        dispatch(
          projectsApi.util.invalidateTags([{ type: "Projects", id: "LIST" }]),
        );
        dispatch(setCurrentProject(newProject));
        dispatch(setCurrentBrand(null));
        dispatch(setBrands([]));
        auth.fetchUser(
          () => {},
          () => {},
        );
        navigate("/brand/new/");
      },
      (err) => {
        setCreateError(getApiErrorMessage(err, "Could not create project"));
        setCreating(false);
      },
    );
  };

  const currentOrganization = currentUser?.organization;
  const canCreateProjects = Boolean(currentUser?.features?.projects);

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-semibold text-theme-text mb-4">
          Account Information
        </h2>
        <div className="text-theme-text opacity-80">
          {authenticated && currentUser ? (
            <div>
              Logged in as{" "}
              <span className="font-medium text-theme-text">
                {currentUser.identity.email}
              </span>
              .{" "}
              <span
                className="text-theme-primary hover:opacity-80 underline cursor-pointer"
                onClick={() => {
                  authService.logout();
                  navigate("/");
                }}
              >
                Log out
              </span>
              .
            </div>
          ) : authenticated ? (
            <div>Fetching user info....</div>
          ) : (
            <div>
              Not logged in yet.{" "}
              <Link
                className="text-theme-primary hover:opacity-80 underline"
                to="/login/"
              >
                Log in
              </Link>
              .
            </div>
          )}
        </div>
      </div>

      {/* Organization Section */}
      {authenticated && currentUser && currentOrganization && (
        <div>
          <h2 className="text-2xl font-semibold text-theme-text mb-4">
            Organization
          </h2>
          <div className="text-theme-text opacity-80">
            {currentOrganization.name}
          </div>
        </div>
      )}

      {/* Projects Section */}
      {authenticated && currentUser && currentOrganization && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-semibold text-theme-text">Projects</h2>
            {canCreateProjects ? (
              <button
                type="button"
                onClick={() => setShowCreateModal(true)}
                className="px-4 py-2 bg-theme-primary text-theme-button rounded-md focus:outline-none focus:ring-2 focus:ring-theme-primary focus:ring-offset-2 hover:opacity-90"
                style={{ backgroundColor: "var(--theme-primary)" }}
              >
                Create New Project
              </button>
            ) : null}
          </div>

          {!projectsLoaded ? (
            <div className="text-theme-text opacity-80">
              Loading projects...
            </div>
          ) : projects.length === 0 ? (
            <div className="text-theme-text opacity-80">
              {canCreateProjects
                ? "No projects yet. Create your first project to get started."
                : "No projects yet."}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {projects.map((project) => {
                const membership = project.memberships?.find(
                  (m) => m.user_id === currentUser.identity.id,
                );
                const role = membership?.role || "member";
                const isActive = currentProject?.hash === project.hash;

                return (
                  <button
                    key={project.hash}
                    type="button"
                    onClick={() => {
                      if (!isActive) {
                        dispatch(setCurrentProject(project));
                      }
                    }}
                    className={`relative p-4 rounded-lg border-2 transition-all text-left flex flex-col h-full w-full ${
                      isActive
                        ? "border-theme-primary"
                        : "border-theme-secondary hover:border-theme-primary hover:border-opacity-50"
                    } bg-theme-main hover:bg-theme-secondary`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-lg font-medium text-theme-text">
                          {project.name}
                        </h3>
                        {isActive && (
                          <svg
                            className="h-5 w-5 text-theme-primary flex-shrink-0"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </div>
                      <p className="text-xs text-theme-text opacity-60 mb-1">
                        Role: <span className="capitalize">{role}</span>
                      </p>
                      <p className="text-xs text-theme-text opacity-60">
                        Created:{" "}
                        {new Date(project.create_date).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="mt-4 pt-4 border-t border-theme-secondary">
                      <div className="flex items-center justify-between text-sm gap-2">
                        <div className="flex flex-col items-center flex-1 min-w-0">
                          <span
                            className={`text-lg font-semibold text-theme-text ${
                              (project.brands_count || 0) === 0
                                ? "opacity-40"
                                : ""
                            }`}
                          >
                            {project.brands_count || 0}
                          </span>
                          <span className="text-xs text-theme-text opacity-70 mt-1">
                            Brands
                          </span>
                        </div>
                        <div className="flex flex-col items-center flex-1 min-w-0">
                          <span
                            className={`text-lg font-semibold text-theme-text ${
                              (project.audiences_count || 0) === 0
                                ? "opacity-40"
                                : ""
                            }`}
                          >
                            {project.audiences_count || 0}
                          </span>
                          <span className="text-xs text-theme-text opacity-70 mt-1">
                            Audiences
                          </span>
                        </div>
                        <div className="flex flex-col items-center flex-1 min-w-0">
                          <span
                            className={`text-lg font-semibold text-theme-text ${
                              (project.scripts_count || 0) === 0
                                ? "opacity-40"
                                : ""
                            }`}
                          >
                            {project.scripts_count || 0}
                          </span>
                          <span className="text-xs text-theme-text opacity-70 mt-1">
                            Scripts
                          </span>
                        </div>
                        <div className="flex flex-col items-center flex-1 min-w-0">
                          <span
                            className={`text-lg font-semibold text-theme-text ${
                              (project.videos_count || 0) === 0
                                ? "opacity-40"
                                : ""
                            }`}
                          >
                            {project.videos_count || 0}
                          </span>
                          <span className="text-xs text-theme-text opacity-70 mt-1">
                            Videos
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Create Project Modal */}
      {showCreateModal && canCreateProjects && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-theme-main border border-theme-secondary rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-theme-text mb-4">
              Create New Project
            </h3>
            {createError && (
              <div className="mb-4 p-3 bg-red-500 bg-opacity-20 border border-red-500 rounded text-sm text-theme-text">
                {createError}
              </div>
            )}
            <form onSubmit={handleCreateProject}>
              <div className="mb-4">
                <label
                  htmlFor="project_name"
                  className="block text-sm font-medium text-theme-text mb-2"
                >
                  Project Name
                </label>
                <input
                  id="project_name"
                  type="text"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  placeholder="My Project"
                  required
                  className="appearance-none block w-full px-3 py-2 border rounded-md shadow-sm text-theme-text placeholder-opacity-60 focus:outline-none focus:ring-theme-primary focus:border-theme-primary sm:text-sm"
                  style={{ borderColor: "var(--theme-text)" }}
                />
              </div>
              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewProjectName("");
                    setCreateError(null);
                  }}
                  className="flex-1 px-4 py-2 border rounded-md text-theme-text hover:bg-theme-secondary focus:outline-none focus:ring-2 focus:ring-theme-primary"
                  style={{ borderColor: "var(--theme-text)" }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="flex-1 px-4 py-2 bg-theme-primary text-theme-button rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-theme-primary focus:ring-offset-2 disabled:opacity-50"
                  style={{ backgroundColor: "var(--theme-primary)" }}
                >
                  {creating ? "Creating..." : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Account;
